import axios from "axios";

import { getApiUrl } from "./config/api.js";
import { APP, APP_ENV, APP_RELEASE } from "./buildInfo.js";
import { installBreadcrumbs, getLastInteraction } from "./breadcrumbs.js";
import { isPageInterrupted, isPageUnloading } from "./errors/pageLifecycle.js";
import { wasErrorReported } from "./errors/reported.js";

const LOCAL_HOSTNAME =
  /^(localhost|127(?:\.\d{1,3}){3}|0\.0\.0\.0|\[?::1\]?|.+\.local|.+\.localhost)$/i;

/**
 * Should this build, on this host, send anything to Monitoring?
 *
 * Monitoring group #1329 was a developer's machine
 * (http://127.0.0.1:5192/retail-circle-admin, release "unknown") posting to
 * the PRODUCTION board — a local build pointed at the prod API reports there
 * like any deployed one. Local hosts and the Vite dev server never report
 * unless VITE_REPORT_LOCAL_ERRORS=true is set to debug the funnel itself.
 */
export function isReportingEnabled({
  hostname = typeof location !== "undefined" ? location.hostname : "",
  env = import.meta.env,
} = {}) {
  if (String(env?.VITE_REPORT_LOCAL_ERRORS ?? "").toLowerCase() === "true") {
    return true;
  }
  if (env?.DEV) return false;
  return !LOCAL_HOSTNAME.test(hostname || "");
}

/**
 * An unhandled rejection that is not worth a Monitoring record:
 *   - an error the API path (showApiError) already reported or deliberately
 *     dropped — the interceptor re-throws it, and a caller with no catch hands
 *     the same object to "unhandledrejection" (groups #1308/#1218/#1216);
 *   - a request the app cancelled;
 *   - a request that got no response because the browser killed it as the tab
 *     hid or unloaded (Kanban opens a card in a NEW tab — group #1003 and the
 *     other "Network Error" groups). classify.js already silences these on the
 *     API path; this is the same rule for the global net.
 * Anything else, including every non-axios rejection, is still reported.
 */
export function isIgnorableRejection(reason) {
  if (!reason || typeof reason !== "object") return false;
  if (wasErrorReported(reason)) return true;
  if (axios.isCancel(reason)) return true;

  const isAxiosError = Boolean(reason.isAxiosError || reason.config);
  if (isAxiosError && !reason.response) {
    return isPageUnloading() || isPageInterrupted();
  }
  return false;
}

// A browser extension runs its content / injected scripts from its own URL
// scheme. Prod RC #166 / DAO #775 ("Failed to connect to MetaMask", first frame
// chrome-extension://nkbihfbeogaeaoehlefnkodbefgpgknn/scripts/inpage.js) was the
// MetaMask extension's own promise rejecting on a visitor without a wallet set
// up — code we neither ship nor can fix.
const EXTENSION_URL_RE =
  /^(?:chrome|moz|safari|safari-web|edge)-extension:\/\//i;
// The URL in one stack frame, any scheme: V8 "at fn (url:1:2)" / "at url:1:2",
// Firefox / Safari "fn@url:1:2".
const FRAME_URL_RE = /([a-z][a-z0-9+.-]*:\/\/[^\s()@]+):\d+:\d+/i;
const V8_FRAME_RE = /^\s*at\s/;

/**
 * The URL of the first stack frame that has one. V8 stacks open with
 * "Name: message" — which can itself quote a URL — so only "at …" lines count
 * there; Firefox / Safari stacks are frames only. Frames with no URL
 * ("<anonymous>", native code) are skipped.
 */
function firstStackFrameUrl(stack) {
  if (typeof stack !== "string" || !stack) return null;
  const lines = stack.split("\n");
  const isV8 = lines.some((line) => V8_FRAME_RE.test(line));
  for (const line of lines) {
    if (isV8 && !V8_FRAME_RE.test(line)) continue;
    const match = FRAME_URL_RE.exec(line);
    if (match) return match[1];
  }
  return null;
}

/**
 * Was this uncaught error / rejection thrown by a browser extension? True only
 * when the event's source file, or the FIRST frame of its stack, is an
 * extension URL. An error thrown by our code that merely passed through an
 * extension deeper in the stack (e.g. a wrapped fetch/listener) is still ours
 * and still reported.
 */
export function isBrowserExtensionError({ filename, stack } = {}) {
  if (typeof filename === "string" && EXTENSION_URL_RE.test(filename)) return true;
  const frameUrl = firstStackFrameUrl(stack);
  return Boolean(frameUrl && EXTENSION_URL_RE.test(frameUrl));
}

/**
 * Single funnel for client-side errors on app.retail-circle.io. Every error —
 * client-visible (red banners), silent, or uncaught (window.onerror /
 * unhandledrejection / ErrorBoundary) — is posted to the backend
 * `/api/log/errors` grouped tables that power the in-app Monitoring page.
 *
 * Sentry is intentionally NOT wired here (deferred on RC for bundle-size
 * reasons); the backend funnel is our system of record. Every path is
 * best-effort: it must never throw and never surface its own banner.
 *
 * @param {Object}  params
 * @param {string}  params.type     short machine label, e.g. "api_error"
 * @param {string} [params.message] human-readable summary
 * @param {Object} [params.context] extra structured fields (status, url, ids, …)
 */
// Drop duplicate reports (same type + status + url) within this window so a
// flaky connection or a grid full of failures can't flood the backend.
const DEDUP_WINDOW_MS = 30000;
const recentReports = new Map();

export function reportClientError({ type, message, context = {} } = {}) {
  if (!isReportingEnabled()) return;

  const dedupKey = `${type}|${context.status ?? ""}|${context.requestUrl || context.src || ""}`;
  const now = Date.now();
  const last = recentReports.get(dedupKey);
  if (last && now - last < DEDUP_WINDOW_MS) {
    return;
  }
  recentReports.set(dedupKey, now);
  if (recentReports.size > 200) {
    for (const [key, ts] of recentReports) {
      if (now - ts >= DEDUP_WINDOW_MS) recentReports.delete(key);
    }
  }

  try {
    const {
      status,
      requestUrl,
      responseMessage,
      user,
      src,
      stack,
      componentStack,
      ...rest
    } = context;

    const extra = { ...rest };
    if (componentStack) extra.componentStack = componentStack;

    axios
      .post(
        getApiUrl("/api/log/errors"),
        {
          error: {
            app: APP,
            environment: APP_ENV,
            release: APP_RELEASE,
            type: type || "unknown",
            message: message || null,
            status: status ?? null,
            requestUrl: requestUrl || src || null,
            responseMessage: responseMessage || null,
            page: typeof location !== "undefined" ? location.href : null,
            interaction: getLastInteraction(),
            stack: stack || null,
            userAgent:
              typeof navigator !== "undefined" ? navigator.userAgent : null,
            user: user ?? null,
            // Legacy field kept for the existing flat-log shape.
            url: typeof location !== "undefined" ? location.href : null,
            context: Object.keys(extra).length ? extra : null,
          },
        },
        { metadata: { silent: true } }
      )
      .catch(() => {});
  } catch (_) {
    // Never let reporting break the calling code path.
  }
}

/**
 * Install the app-wide safety net: interaction breadcrumbs + global handlers for
 * uncaught errors and unhandled promise rejections. Call once at startup.
 */
let installed = false;
export function installGlobalErrorHandlers() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  installBreadcrumbs();

  window.addEventListener("error", (event) => {
    // Resource-load failures (img/script) surface here with no `error` object;
    // only capture genuine uncaught JS errors to keep the board signal.
    if (!event.error) return;
    if (
      isBrowserExtensionError({ filename: event.filename, stack: event.error.stack })
    ) {
      return;
    }
    reportClientError({
      type: "window_error",
      message: event.error.message || event.message || "Uncaught error",
      context: {
        stack: event.error.stack || null,
        src: event.filename
          ? `${event.filename}:${event.lineno}:${event.colno}`
          : null,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    if (isIgnorableRejection(reason)) return;
    if (reason && isBrowserExtensionError({ stack: reason.stack })) return;
    reportClientError({
      type: "unhandled_rejection",
      message:
        (reason && (reason.message || String(reason))) ||
        "Unhandled promise rejection",
      context: { stack: (reason && reason.stack) || null },
    });
  });
}
